import { useState } from 'react';
import { motion } from 'framer-motion';
import { UserRound } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import { Button } from './Button';

interface NamePromptProps {
  /** Copy above the field. Defaults to the generic join-flow wording. */
  subtitle?: string;
  onSaved?: () => void;
}

/**
 * Inline "Como você quer ser chamado?" form.
 *
 * Magic-link signups arrive with `name: null`, and ranking standings filter out
 * nameless players — so anywhere a user is about to appear in a ranking we ask
 * for a display name first. Render this only when `!subscriber?.name`.
 */
export function NamePrompt({ subtitle, onSaved }: NamePromptProps) {
  const { refreshSubscriber } = useAuth();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError('Digite seu nome');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await api.auth.updateProfile({ name: trimmed });
      if (!response.success) {
        setError(response.error?.message || 'Erro ao salvar nome');
        return;
      }
      await refreshSubscriber();
      onSaved?.();
    } catch {
      setError('Erro de conexão');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-left space-y-3"
    >
      <div className="flex items-center gap-2 text-white/70">
        <UserRound className="w-4 h-4 text-turquoise" />
        <p className="text-sm font-medium">Como você quer ser chamado?</p>
      </div>
      <p className="text-white/40 text-xs">
        {subtitle || 'Esse é o nome que os outros jogadores vão ver no ranking.'}
      </p>
      <input
        type="text"
        autoFocus
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
        }}
        placeholder="Seu nome"
        maxLength={60}
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-turquoise/50"
      />
      {error && <p className="text-coral text-xs">{error}</p>}
      <Button onClick={handleSave} fullWidth isLoading={isSaving} size="md">
        Continuar
      </Button>
    </motion.div>
  );
}
